import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import { isTicketOpen } from './crmConstants';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import StitchLayout from '../../components/Crm/CrmLayout';
import { Spin, Empty, Tooltip, Button, Tag, Dropdown, Pagination, DatePicker } from 'antd';
import moment from 'moment';
import type { Moment } from 'moment';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import {
    FilterOutlined,
    MailOutlined,
    PhoneOutlined,
    MessageOutlined,
    RobotOutlined,
    GlobalOutlined,
    ExportOutlined,
    ReloadOutlined,
    IdcardOutlined,
    WhatsAppOutlined,
    MoreOutlined,
    EyeOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
} from '@ant-design/icons';
import PhoneCallDetailsModal from '../../components/Crm/PhoneCallDetailsModal';
const FILTER_SESSION_KEY = 'escalations_filters';

const readSessionFilters = (): { startDate: string; endDate: string; activeQuick: string | null } | null => {
    try {
        const raw = sessionStorage.getItem(FILTER_SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeSessionFilters = (startDate: Moment, endDate: Moment, activeQuick: string | null) => {
    try {
        sessionStorage.setItem(
            FILTER_SESSION_KEY,
            JSON.stringify({
                startDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
                endDate: endDate.format('YYYY-MM-DD HH:mm:ss'),
                activeQuick,
            }),
        );
    } catch {
        //Silently ignore sessionStorage write failures
    }
};

const QUICK_FILTERS = [
    { label: 'Today', days: 0 },
    { label: 'Yesterday', days: 1 },
    { label: 'Last 3 Days', days: 3 },
    { label: 'Last 7 Days', days: 7 },
];

const { RangePicker } = DatePicker;

interface TicketGroup {
    _id: number;
    all_tickets: any[];
    open_tickets: any[];
    total_tickets_count: number;
    user_info: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        id: number;
    };
}

interface EscalationsApiResponse {
    status: boolean;
    message: string;
    data: TicketGroup[];
    pagination: {
        current_page: number;
        per_page: number;
        total_records: number;
        total_pages: number;
    };
}

const getPlatformActivity = (tickets: any[]): Record<string, number> => {
    const counts: Record<string, number> = { email: 0, portal: 0, phone: 0, saharaVoiceBot: 0, whatsappChatbot: 0, setuChatbot: 0 };
    tickets.forEach(t => {
        const department = t.custom_fields?.cf_department || '';
        if (department === 'Sahara Voice Bot') {
            counts.saharaVoiceBot += 1;
        }
        else if (department === "ChatBot") {
            counts.whatsappChatbot += 1;
        }
        else if (department === "Setu Chat Bot") {
            counts.setuChatbot += 1;
        }
        else if (t.source === 1 || t.email_config_id) counts.email += 1;
        else if (t.source === 3) counts.phone += 1;
        else counts.portal += 1;
    });

    return counts;
};

const ActivityIcons = ({ tickets, onPhoneDetailsClick }: { tickets: any[]; onPhoneDetailsClick?: () => void }) => {
    const act = getPlatformActivity(tickets);
    const icons: { key: string; icon: React.ReactNode; label: string; bgColor: string; textColor: string; countBg: string }[] = [
        { key: 'whatsappChatbot', icon: <WhatsAppOutlined />, label: 'WhatsApp Chat Bot', bgColor: 'bg-green-500', textColor: 'text-white', countBg: 'bg-green-600' },
        { key: 'saharaVoiceBot', icon: <RobotOutlined />, label: 'Sahara Voice Bot', bgColor: 'bg-purple-500', textColor: 'text-white', countBg: 'bg-purple-600' },
        { key: 'setuChatbot', icon: <MessageOutlined />, label: 'Setu Chat Bot', bgColor: 'bg-indigo-500', textColor: 'text-white', countBg: 'bg-indigo-600' },
        { key: 'email', icon: <MailOutlined />, label: 'Email', bgColor: 'bg-blue-500', textColor: 'text-white', countBg: 'bg-blue-600' },
        { key: 'phone', icon: <PhoneOutlined />, label: 'Phone', bgColor: 'bg-emerald-500', textColor: 'text-white', countBg: 'bg-emerald-600' },
        { key: 'portal', icon: <GlobalOutlined />, label: 'Portal', bgColor: 'bg-orange-500', textColor: 'text-white', countBg: 'bg-orange-600' },
    ];

    const phoneMenuItems: MenuProps['items'] = [
        {
            key: 'view-call-details',
            icon: <EyeOutlined />,
            label: 'View Call Details',
        },
    ];

    return (
        <div className="flex items-center gap-2">
            {icons.filter(i => act[i.key] > 0).map(i => (
                <div key={i.key} className="relative flex items-center gap-1">
                    <Tooltip title={`${i.label}: ${act[i.key]}`}>
                        <div className="relative">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${i.bgColor} ${i.textColor} text-xs hover:scale-110 transition-all duration-200 shadow-sm`}>
                                {i.icon}
                            </div>
                            <span className={`absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full ${i.countBg} text-white text-[9px] flex items-center justify-center font-bold shadow-sm`}>
                                {act[i.key]}
                            </span>
                        </div>
                    </Tooltip>
                    {/*Dropdown menu for phone source — allows viewing call details */}
                    {i.key === 'phone' && onPhoneDetailsClick && (
                        <Dropdown
                            menu={{
                                items: phoneMenuItems,
                                onClick: ({ key, domEvent }) => {
                                    domEvent.stopPropagation();
                                    if (key === 'view-call-details') {
                                        onPhoneDetailsClick();
                                    }
                                },
                            }}
                            trigger={['click']}
                            placement="bottomLeft"
                        >
                            <button
                                onClick={(e) => e.stopPropagation()}
                                className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-800 transition-all ml-1"
                            >
                                <MoreOutlined className="text-[10px]" />
                            </button>
                        </Dropdown>
                    )}
                </div>
            ))}
        </div>
    );
};

const Escalations = () => {
    const [data, setData] = useState<TicketGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [callDetailsModal, setCallDetailsModal] = useState<{ visible: boolean; customerPhone?: string }>({ visible: false });
    //Initialize date range and active quick filter from sessionStorage if available
    const [startDate, setStartDate] = useState<Moment>(() => {
        const saved = readSessionFilters();
        return saved ? moment(saved.startDate) : moment().startOf('day');
    });
    const [endDate, setEndDate] = useState<Moment>(() => {
        const saved = readSessionFilters();
        return saved ? moment(saved.endDate) : moment().endOf('day');
    });
    //Active quick filter label restored from session or defaults to Today
    const [activeQuick, setActiveQuick] = useState<string | null>(() => {
        const saved = readSessionFilters();
        return saved ? saved.activeQuick : 'Today';
    });
    //Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    //Sort direction states for Total Tickets and Open Issues columns — mutually exclusive
    const [ticketSort, setTicketSort] = useState<'asc' | 'desc' | null>(null);
    const [openSort, setOpenSort] = useState<'asc' | 'desc' | null>(null);
    //totalPages tracks API pagination data though not directly rendered
    const [totalPages, setTotalPages] = useState(0); // eslint-disable-line @typescript-eslint/no-unused-vars
    //Map of customerId to priorityStatus (0/1) or false (not priority) — populated lazily per page
    const [priorityMap, setPriorityMap] = useState<Record<number, number | false>>({});
    const [loadingPriority, setLoadingPriority] = useState(false);

    //Handle phone details click
    const handlePhoneDetailsClick = (customerPhone?: string) => {
        setCallDetailsModal({ visible: true, customerPhone });
    };

    //Apply a quick filter shortcut, computing date boundaries from day offset and persisting to session
    const applyQuickFilter = useCallback((label: string, days: number) => {
        let newStart: Moment;
        let newEnd: Moment;
        if (days === 0) {
            newStart = moment().startOf('day');
            newEnd = moment().endOf('day');
        } else if (days === 1) {
            newStart = moment().subtract(1, 'day').startOf('day');
            newEnd = moment().subtract(1, 'day').endOf('day');
        } else {
            newStart = moment().subtract(days, 'days').startOf('day');
            newEnd = moment().endOf('day');
        }
        setActiveQuick(label);
        setStartDate(newStart);
        setEndDate(newEnd);
        setCurrentPage(1);
        writeSessionFilters(newStart, newEnd, label);
    }, []);

    //Handle date change and reset to first page
    const handleDateChange = () => {
        setCurrentPage(1);
    };

    // Download all escalations as CSV
    const [downloading, setDownloading] = useState(false);
    const handleDownloadCSV = async () => {
        setDownloading(true);
        try {
            const params = new URLSearchParams();
            params.append('start_date', startDate.format('YYYY-MM-DD'));
            params.append('end_date', endDate.format('YYYY-MM-DD'));
            params.append('page', '1');
            params.append('per_page', '1000');
            if (ticketSort) {
                params.append('sort_by', 'total_tickets');
                params.append('sort_order', ticketSort);
            } else if (openSort) {
                params.append('sort_by', 'open_tickets');
                params.append('sort_order', openSort);
            }
            const url = `${apis.FRESHDESK_ESCALATIONS_GROUPED}?${params.toString()}`;
            const res = await axiosInstance.get(url);
            const apiResponse: EscalationsApiResponse = res.data;
            const groups = apiResponse.data || [];

            // Transform data for CSV
            const csvRows = [
                [
                    'customer_id',
                    'email',
                    'phone',
                    'all_tickets',
                    'open_tickets',
                    'open_tickets_count',
                    'total_tickets_count',
                ],
            ];
            groups.forEach((g: any) => {
                csvRows.push([
                    g.user_info?.id ?? '',
                    g.user_info?.email ?? '',
                    g.user_info?.phone ?? '',
                    (g.all_tickets || []).map((t: any) => t.id || t.ticket_id || '').join(','),
                    (g.open_tickets || []).map((t: any) => t.id || t.ticket_id || '').join(','),
                    g.open_tickets?.length ?? 0,
                    g.total_tickets_count ?? 0,
                ]);
            });
            const csvContent = csvRows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const urlBlob = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = urlBlob;
            a.download = `escalations_${startDate.format('YYYYMMDD')}_to_${endDate.format('YYYYMMDD')}.csv`;
            a.click();
            URL.revokeObjectURL(urlBlob);
        } catch (err) {
            alert('Failed to download CSV');
        } finally {
            setDownloading(false);
        }
    };

    //Reset all filters to today and clear sessionStorage entry
    const handleResetFilters = useCallback(() => {
        const newStart = moment().startOf('day');
        const newEnd = moment().endOf('day');
        setStartDate(newStart);
        setEndDate(newEnd);
        setActiveQuick('Today');
        setCurrentPage(1);
        sessionStorage.removeItem(FILTER_SESSION_KEY);
    }, []);

    //Handle pagination change
    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size && size !== pageSize) {
            setPageSize(size);
        }
    };

    //Fetch grouped ticket data when date range, pagination, or sort changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError('');
            try {
                //Build query params including optional sort direction for backend ordering
                const params = new URLSearchParams();
                params.append('start_date', startDate.format('YYYY-MM-DD'));
                params.append('end_date', endDate.format('YYYY-MM-DD'));
                params.append('page', currentPage.toString());
                params.append('per_page', pageSize.toString());
                //Only one sort column is active at a time — mutually exclusive
                if (ticketSort) {
                    params.append('sort_by', 'total_tickets');
                    params.append('sort_order', ticketSort);
                } else if (openSort) {
                    params.append('sort_by', 'open_tickets');
                    params.append('sort_order', openSort);
                }

                const url = `${apis.FRESHDESK_ESCALATIONS_GROUPED}?${params.toString()}`;
                const res = await axiosInstance.get(url);
                const apiResponse: EscalationsApiResponse = res.data;

                // Handle both old and new response formats
                if (apiResponse.data && apiResponse.pagination) {
                    //New paginated response format
                    setData(apiResponse.data);
                    setTotalRecords(apiResponse.pagination.total_records);
                    setTotalPages(apiResponse.pagination.total_pages);
                } else {
                    //Fallback for old response format
                    const fallbackData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                    setData(fallbackData);
                    setTotalRecords(fallbackData.length);
                    setTotalPages(1);
                }
            } catch {
                setError('Failed to load escalations.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [startDate, endDate, currentPage, pageSize, ticketSort, openSort]);

    //Lazy batch priority check — fires once per page after main data loads, clears previous map
    useEffect(() => {
        if (data.length === 0) { setPriorityMap({}); return; }
        //Collect all customer IDs visible on current page for a single bulk request
        const customerIds = data.map(g => g._id).filter(Boolean);
        setPriorityMap({});
        setLoadingPriority(true);
        axiosInstance.post(apis.CHECK_CUSTOMER_PRIORITY_ORDERS, {
            orderId: [], pageNumber: 1, pageCount: 200, itemId: [], itemStatus: [],
            supplierId: [], customerId: customerIds, email: '', phone: '', awb: [],
            forwardAwb: [], shipperId: [], returnId: [], productRef: '', shipmentId: [],
            clusterList: [], categoryList: [], countryCode: 'IN',
        })
            .then(res => {
                const arr: any[] = res.data?.data || [];
                //Build a map of customerId to lowest priorityStatus found
                const map: Record<number, number> = {};
                arr.forEach((d: any) => {
                    const cid = d.customerId;
                    if (cid == null) return;
                    if (map[cid] === undefined || d.priorityStatus < map[cid]) {
                        map[cid] = d.priorityStatus;
                    }
                });
                //Mark non-priority IDs explicitly as false
                const full: Record<number, number | false> = {};
                customerIds.forEach(id => { full[id] = map[id] !== undefined ? map[id] : false; });
                setPriorityMap(full);
            })
            .catch(() => {
                const fallback: Record<number, number | false> = {};
                customerIds.forEach(id => { fallback[id] = false; });
                setPriorityMap(fallback);
            })
            .finally(() => setLoadingPriority(false));
    }, [data]);

    //Persist date range and active quick filter to sessionStorage whenever they change
    useEffect(() => {
        writeSessionFilters(startDate, endDate, activeQuick);
    }, [startDate, endDate, activeQuick]);

    //Pass data through as-is — ordering is handled by the backend
    const enriched = useMemo(() => data, [data]);

    //Toggle Total Tickets sort and clear Open Issues sort — backend re-fetches from page 1
    const handleTicketSortToggle = () => {
        setCurrentPage(1);
        setOpenSort(null);
        setTicketSort(prev => prev === null ? 'desc' : prev === 'desc' ? 'asc' : 'desc');
    };

    //Toggle Open Issues sort and clear Total Tickets sort — backend re-fetches from page 1
    const handleOpenSortToggle = () => {
        setCurrentPage(1);
        setTicketSort(null);
        setOpenSort(prev => prev === null ? 'desc' : prev === 'desc' ? 'asc' : 'desc');
    };

    return (
        <StitchLayout>
            {/*Escalations page wrapper with dark theme */}
            <div className="min-h-full" style={{ background: 'transparent' }}>

                {/*Page header */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-gray-900 mb-0.5">Customer Escalations</h1>
                        <p className="text-sm text-gray-500">
                            List of customer accounts
                            {totalRecords > 0 && (
                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                    {totalRecords} total
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadCSV}
                            size="small"
                            loading={downloading}
                            disabled={downloading}
                        >
                            {downloading ? 'Downloading...' : 'Download CSV'}
                        </Button>
                        {/*Ant Design RangePicker bound to the moment-based state */}
                        <RangePicker
                            value={[
                                dayjs(startDate.format('YYYY-MM-DD'), 'YYYY-MM-DD'),
                                dayjs(endDate.format('YYYY-MM-DD'), 'YYYY-MM-DD'),
                            ]}
                            onChange={(vals) => {
                                if (vals && vals[0] && vals[1]) {
                                    const newStart = moment(vals[0].format('YYYY-MM-DD')).startOf('day');
                                    const newEnd = moment(vals[1].format('YYYY-MM-DD')).endOf('day');
                                    setStartDate(newStart);
                                    setEndDate(newEnd);
                                    setActiveQuick(null);
                                    handleDateChange();
                                }
                            }}
                            disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
                            format="YYYY-MM-DD"
                            allowClear={false}
                        />
                        {/*Reset Filters CTA — clears sessionStorage and resets to Today */}
                        <Button
                            icon={<FilterOutlined />}
                            onClick={handleResetFilters}
                            className="flex-shrink-0"
                            title="Reset filters to today"
                        >
                            Reset Filters
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => window.location.reload()}
                            className="flex-shrink-0"
                        >
                            Refresh
                        </Button>
                    </div>
                </div>

                {/*Quick filter pill buttons row matching Ozonetel Call Track pattern */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className="text-xs text-gray-500 font-medium mr-1">Quick:</span>
                    {QUICK_FILTERS.map(({ label, days }) => (
                        <button
                            key={label}
                            onClick={() => applyQuickFilter(label, days)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer ${activeQuick === label
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/*Loading state */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-24 gap-4">
                        <Spin size="large" />
                        <p className="text-gray-400 text-sm">Loading escalations...</p>
                    </div>
                )}

                {/*Error state */}
                {error && (
                    <div className="text-red-500 bg-red-50 border border-red-200 rounded-xl p-6 text-center">{error}</div>
                )}

                {/*Main content */}
                {!loading && !error && (
                    <>
                        {enriched.length === 0 ? (
                            <Empty description="No escalations found" className="py-16" />
                        ) : (
                            //Escalation table
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                {/*Table header row with clickable sort on Total Tickets */}
                                <div className="grid gap-4 px-6 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100"
                                    style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr' }}
                                >
                                    <span>Customer</span>
                                    <span>Platform Activity</span>
                                    {/*Clickable Total Tickets header with asc/desc sort icon */}
                                    <button
                                        onClick={handleTicketSortToggle}
                                        className="flex items-center gap-1 group/sort cursor-pointer select-none text-left"
                                    >
                                        <span className={ticketSort ? 'text-blue-600' : 'text-gray-400 group-hover/sort:text-gray-600'}>Total Tickets</span>
                                        <span className={`ml-0.5 transition-all duration-200 ${ticketSort === 'asc'
                                            ? 'text-blue-600'
                                            : ticketSort === 'desc'
                                                ? 'text-blue-600'
                                                : 'text-gray-300 group-hover/sort:text-gray-400'
                                            }`}>
                                            {ticketSort === 'asc'
                                                ? <ArrowUpOutlined className="text-[10px]" />
                                                : <ArrowDownOutlined className="text-[10px]" />
                                            }
                                        </span>
                                    </button>
                                    {/*Clickable Open Issues header with asc/desc sort icon */}
                                    <button
                                        onClick={handleOpenSortToggle}
                                        className="flex items-center gap-1 group/opensort cursor-pointer select-none text-left"
                                    >
                                        <span className={openSort ? 'text-blue-600' : 'text-gray-400 group-hover/opensort:text-gray-600'}>Open Issues</span>
                                        <span className={`ml-0.5 transition-all duration-200 ${openSort ? 'text-blue-600' : 'text-gray-300 group-hover/opensort:text-gray-400'
                                            }`}>
                                            {openSort === 'asc'
                                                ? <ArrowUpOutlined className="text-[10px]" />
                                                : <ArrowDownOutlined className="text-[10px]" />
                                            }
                                        </span>
                                    </button>
                                </div>

                                {/*Table rows */}
                                {enriched.map((group, idx) => {
                                    const name = `${group.user_info?.firstName || ''} ${group.user_info?.lastName || ''}`.trim() || `User#${group._id}`;
                                    //Sync ticket counts with Interaction Timeline using same isTicketOpen logic
                                    const allTickets: any[] = group.all_tickets || [];
                                    const openTickets: any[] = allTickets.filter((t: any) => isTicketOpen(t.status));
                                    const totalCount = allTickets.length;
                                    const openCount = openTickets.length;

                                    return (
                                        <div
                                            key={group._id}
                                            className={`grid gap-4 px-6 py-4 items-center hover:bg-blue-50 hover:shadow-sm transition-all cursor-pointer group ${idx < enriched.length - 1 ? 'border-b border-gray-100' : ''
                                                }`}
                                            style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr' }}
                                            onClick={() => {
                                                const url = group.user_info?.phone
                                                    ? `/crm/interaction-timeline?phone_number=${group.user_info.phone}`
                                                    : `/crm/interaction-timeline?user_id=${group._id}`;
                                                window.open(url, '_blank');
                                            }}
                                        >
                                            {/*Customer cell with name, id, phone, email */}
                                            <div className="flex items-center gap-3">
                                                {/*Customer avatar with first letter of name */}
                                                <div
                                                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                                                    style={{ backgroundColor: '#6366f1' }}
                                                >
                                                    {name[0]?.toUpperCase() || '?'}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <p className="text-sm font-semibold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors">{name}</p>
                                                        {/*Priority pill — pulsing while loading, then P0/P1 or nothing */}
                                                        {loadingPriority && priorityMap[group._id] === undefined && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border bg-gray-100 text-gray-300 border-gray-200 animate-pulse">P?</span>
                                                        )}
                                                        {!loadingPriority && priorityMap[group._id] === 0 && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border bg-red-100 text-red-600 border-red-300">⭐P0</span>
                                                        )}
                                                        {!loadingPriority && priorityMap[group._id] === 1 && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border bg-orange-100 text-orange-600 border-orange-300">🌟P1</span>
                                                        )}
                                                    </div>
                                                    {/*Display phone or user ID without individual links since whole row is clickable */}
                                                    {group.user_info?.phone ? (
                                                        <p className="text-[10px] leading-tight flex items-center gap-1 text-gray-400">
                                                            <PhoneOutlined className="text-gray-300" />
                                                            <span className="font-medium">{group.user_info.phone}</span>
                                                        </p>
                                                    ) : (
                                                        <p className="text-[10px] leading-tight flex items-center gap-1 text-gray-400">
                                                            <IdcardOutlined className="text-gray-300" />
                                                            <span className="font-medium">#{group._id}</span>
                                                        </p>
                                                    )}
                                                    {group.user_info?.email && (
                                                        <p className="text-[10px] leading-tight flex items-center gap-1 text-gray-400">
                                                            <MailOutlined className="text-gray-300 flex-shrink-0" />
                                                            <span className="truncate max-w-[140px]">{group.user_info.email}</span>
                                                        </p>
                                                    )}

                                                </div>
                                            </div>

                                            {/*Platform activity icons filtered to open tickets only */}
                                            <ActivityIcons
                                                tickets={(group.all_tickets || []).filter((t: any) => isTicketOpen(t.status))}
                                                onPhoneDetailsClick={() => handlePhoneDetailsClick(group.user_info?.phone)}
                                            />

                                            {/*Total tickets count with ticket ID pills listing all ticket IDs */}
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                                                    {totalCount}
                                                    <Tooltip title="View all tickets">
                                                        <ExportOutlined className="text-gray-400 text-xs cursor-pointer hover:text-blue-500" />
                                                    </Tooltip>
                                                </div>
                                                {/*Ticket ID pills with smaller created_at date for all tickets */}
                                                <div className="flex flex-wrap gap-1">
                                                    {allTickets.slice(0, 5).map((t: any) => (
                                                        <Tooltip key={t.id} title={t.created_at ? new Date(t.created_at).toLocaleString() : ''}>
                                                            <a
                                                                href={`/crm/freshdesk-tickets/${t.id}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="inline-flex flex-col items-start px-1.5 py-0.5 rounded bg-gray-100 hover:bg-blue-100 hover:text-blue-700 text-gray-500 transition-colors border border-gray-200 leading-none"
                                                            >
                                                                <span className="text-[10px] font-mono font-medium">#{t.id}</span>
                                                                {/*Smaller muted created_at date below ticket ID */}
                                                                {t.created_at && (
                                                                    <span className="text-[8px] text-gray-400 mt-0.5">
                                                                        {new Date(t.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                                                    </span>
                                                                )}
                                                            </a>
                                                        </Tooltip>
                                                    ))}
                                                    {allTickets.length > 5 && (
                                                        <span className="text-[9px] text-gray-400 self-center">+{allTickets.length - 5} more</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/*Open tickets badge with ticket ID pills for open tickets only */}
                                            <div className="flex flex-col gap-1">
                                                <Tag
                                                    color={openCount > 5 ? 'red' : openCount > 2 ? 'orange' : 'blue'}
                                                    className="font-semibold text-xs self-start"
                                                >
                                                    {openCount} Tickets
                                                </Tag>
                                                {/*Open ticket ID pills with smaller created_at date */}
                                                <div className="flex flex-wrap gap-1">
                                                    {openTickets.slice(0, 5).map((t: any) => (
                                                        <Tooltip key={t.id} title={t.created_at ? new Date(t.created_at).toLocaleString() : ''}>
                                                            <a
                                                                href={`/crm/freshdesk-tickets/${t.id}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="inline-flex flex-col items-start px-1.5 py-0.5 rounded bg-orange-50 hover:bg-orange-100 hover:text-orange-700 text-orange-500 transition-colors border border-orange-200 leading-none"
                                                            >
                                                                <span className="text-[10px] font-mono font-medium">#{t.id}</span>
                                                                {/*Smaller muted created_at date below open ticket ID */}
                                                                {t.created_at && (
                                                                    <span className="text-[8px] text-orange-300 mt-0.5">
                                                                        {new Date(t.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                                                    </span>
                                                                )}
                                                            </a>
                                                        </Tooltip>
                                                    ))}
                                                    {openTickets.length > 5 && (
                                                        <span className="text-[9px] text-gray-400 self-center">+{openTickets.length - 5} more</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/*Pagination component */}
                        {enriched.length > 0 && (
                            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                                <div className="text-sm text-gray-500">
                                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} escalations
                                </div>
                                <Pagination
                                    current={currentPage}
                                    total={totalRecords}
                                    pageSize={pageSize}
                                    showSizeChanger
                                    showQuickJumper
                                    pageSizeOptions={['10', '20', '50', '100']}
                                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                    onChange={handlePageChange}
                                    onShowSizeChange={handlePageChange}
                                    className="text-sm"
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/*Phone call details modal — lists calls then opens OzonetelCallDetails */}
            <PhoneCallDetailsModal
                visible={callDetailsModal.visible}
                phone={callDetailsModal.customerPhone}
                onClose={() => setCallDetailsModal({ visible: false })}
            />
        </StitchLayout>
    );
};

export default Escalations;
